
// Production-ready logging utility with PII sanitization
type LogLevel = 'error' | 'warn' | 'info' | 'debug';

interface LoggerConfig {
  enableInProduction: boolean;
  level: LogLevel;
  sanitizeData: boolean;
}

const config: LoggerConfig = {
  enableInProduction: false, // Disable logs in production by default
  level: import.meta.env.DEV ? 'debug' : 'error',
  sanitizeData: true
};

// Sensitive data patterns to sanitize
const SENSITIVE_PATTERNS = {
  email: /[\w.-]+@[\w.-]+\.\w+/g,
  userId: /user_id['":\s]*['"]\w{8}-\w{4}-\w{4}-\w{4}-\w{12}['"]/gi,
  token: /token['":\s]*['"]\w+['"]/gi,
  password: /password['":\s]*['"]\w+['"]/gi,
  auth: /auth['":\s]*['"]\w+['"]/gi,
  supabaseUrl: /https:\/\/\w+\.supabase\.co/g
};

const sanitizeData = (data: any): any => {
  if (!config.sanitizeData) return data;
  
  if (typeof data === 'string') {
    let sanitized = data;
    Object.entries(SENSITIVE_PATTERNS).forEach(([key, pattern]) => {
      sanitized = sanitized.replace(pattern, `[${key.toUpperCase()}_REDACTED]`);
    });
    return sanitized;
  }
  
  if (typeof data === 'object' && data !== null) {
    const sanitized: any = Array.isArray(data) ? [] : {};
    Object.keys(data).forEach(key => {
      if (['email', 'password', 'token', 'auth'].some(sensitive => 
        key.toLowerCase().includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeData(data[key]);
      }
    });
    return sanitized;
  }
  
  return data;
};

const shouldLog = (level: LogLevel): boolean => {
  if (!import.meta.env.DEV && !config.enableInProduction) {
    return level === 'error'; // Only errors in production
  }
  
  const levels = ['error', 'warn', 'info', 'debug'];
  return levels.indexOf(level) <= levels.indexOf(config.level);
};

export const logger = {
  error: (message: string, data?: any) => {
    if (shouldLog('error')) {
      const sanitizedData = data ? sanitizeData(data) : undefined;
      if (sanitizedData) {
        console.error(`[ERROR] ${message}`, sanitizedData);
      } else {
        console.error(`[ERROR] ${message}`);
      }
    }
  },
  
  warn: (message: string, data?: any) => {
    if (shouldLog('warn')) {
      const sanitizedData = data ? sanitizeData(data) : undefined;
      if (sanitizedData) {
        console.warn(`[WARN] ${message}`, sanitizedData);
      } else {
        console.warn(`[WARN] ${message}`);
      }
    }
  },
  
  info: (message: string, data?: any) => {
    if (shouldLog('info')) {
      const sanitizedData = data ? sanitizeData(data) : undefined;
      if (sanitizedData) {
        console.info(`[INFO] ${message}`, sanitizedData);
      } else {
        console.info(`[INFO] ${message}`);
      }
    }
  },
  
  debug: (message: string, data?: any) => {
    if (shouldLog('debug')) {
      const sanitizedData = data ? sanitizeData(data) : undefined;
      if (sanitizedData) {
        console.log(`[DEBUG] ${message}`, sanitizedData);
      } else {
        console.log(`[DEBUG] ${message}`);
      }
    }
  }
};
